import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { FadeUp, LineReveal } from "@/components/Reveal";

const serviceOptions = [
  "Collision Repair",
  "Vehicle Restoration",
  "Custom Paint & Refinish",
  "Dent & Scratch Repair",
  "Detailing",
  "Other / Not Sure",
];

const empty = { name: "", email: "", phone: "", vehicle: "", service: "", preferred_date: "", message: "" };

export default function Book() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/appointments`, form);
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="book-page">
      <section className="px-6 md:px-12 pt-40 md:pt-52 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-6">Book Appointment</p>
            </FadeUp>
            <h1 className="font-display uppercase leading-[0.9] text-[14vw] lg:text-7xl">
              <LineReveal text="Get Your" delay={0.1} />
              <LineReveal text="Vehicle In" delay={0.25} className="text-outline" />
            </h1>
            <FadeUp delay={0.4}>
              <p className="mt-8 text-zinc-400 text-sm md:text-base leading-relaxed max-w-md">
                Tell us about your vehicle and pick a day that works. We'll confirm your
                appointment by phone or email — usually the same business day.
              </p>
              <div className="mt-10 border-l-2 border-[#E51B23] pl-6 space-y-2 text-sm text-zinc-500">
                <p>Mon – Fri, 8:00 AM – 5:30 PM</p>
                <p>(250) 864-5030</p>
                <p>2323 Hunter Rd, Unit #7, Kelowna, BC</p>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.2} className="lg:col-span-7">
            {done ? (
              <div className="border border-zinc-800 bg-[#131A2E] p-10 md:p-14 text-center" data-testid="booking-success">
                <CheckCircle2 size={48} className="text-[#E51B23] mx-auto" />
                <h2 className="font-display uppercase text-3xl md:text-4xl mt-6">Request Received</h2>
                <p className="text-zinc-400 text-sm mt-4 max-w-md mx-auto leading-relaxed">
                  Thanks {done.name} — we've got your request for {done.service} on your{" "}
                  {done.vehicle}. We'll be in touch shortly to confirm your spot.
                </p>
                <div className="mt-8 flex justify-center gap-4 flex-wrap">
                  <Link to="/#work" data-testid="success-view-work-link" className="osc-btn-outline">See Our Work</Link>
                  <button
                    data-testid="book-another-button"
                    onClick={() => { setDone(null); setForm(empty); }}
                    className="osc-btn-primary"
                  >
                    Book Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-zinc-800 bg-[#131A2E] p-8 md:p-12" data-testid="booking-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="osc-label" htmlFor="book-name">Full Name</label>
                    <input id="book-name" data-testid="book-name-input" required className="osc-input" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className="osc-label" htmlFor="book-phone">Phone</label>
                    <input id="book-phone" data-testid="book-phone-input" required type="tel" className="osc-input" value={form.phone} onChange={set("phone")} placeholder="(250) 555-0000" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="osc-label" htmlFor="book-email">Email</label>
                    <input id="book-email" data-testid="book-email-input" required type="email" className="osc-input" value={form.email} onChange={set("email")} placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="osc-label" htmlFor="book-vehicle">Vehicle (Year / Make / Model)</label>
                    <input id="book-vehicle" data-testid="book-vehicle-input" required className="osc-input" value={form.vehicle} onChange={set("vehicle")} placeholder="1967 Ford Mustang" />
                  </div>
                  <div>
                    <label className="osc-label" htmlFor="book-service">Service Needed</label>
                    <select id="book-service" data-testid="book-service-select" required className="osc-input" value={form.service} onChange={set("service")}>
                      <option value="" disabled>Select a service</option>
                      {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="osc-label" htmlFor="book-date">Preferred Date</label>
                    <input id="book-date" data-testid="book-date-input" required type="date" className="osc-input" value={form.preferred_date} onChange={set("preferred_date")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="osc-label" htmlFor="book-message">Anything We Should Know?</label>
                    <textarea id="book-message" data-testid="book-message-input" rows={4} className="osc-input resize-none" value={form.message} onChange={set("message")} placeholder="Describe the damage or the project..." />
                  </div>
                </div>

                {error && <p className="mt-6 text-sm text-red-400" data-testid="booking-error">{error}</p>}

                <button
                  type="submit"
                  data-testid="booking-submit-button"
                  disabled={loading}
                  className="osc-btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Request Appointment"}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
